import { Injectable } from "@angular/core";
import { SelectionItem } from "./selection-item";
import { SelectionStrategySelectAll } from "./selection-strategies/SelectionStrategySelectAll";
import { SelectionStrategySelectFirstLeafIfOnlyOne } from "./selection-strategies/SelectionStrategySelectFirstLeafIfOnlyOne";
import { SelectionEvent } from "./SelectionEvent";
import { SelectionEventType } from "./SelectionEventType";
import { SelectionStrategyType } from "./SelectionStrategyType";
import { ValueStrategyType } from "./ValueStrategyType";
@Injectable({
    providedIn: "root"
})
export class TreeSelectorService {

    syncSelectionItems(
        values: any[],
        selectionItems: SelectionItem[],
        compare: (a, b) => boolean,
        strategyType: SelectionStrategyType,
        preSelected = false): boolean {
        switch (strategyType) {
            case SelectionStrategyType.SelectAll: {
                return SelectionStrategySelectAll.syncSelectionItems(values, selectionItems, compare, preSelected);
            }
            case SelectionStrategyType.SelectFirstLeafIfOnlyOne: {
                return SelectionStrategySelectFirstLeafIfOnlyOne.syncSelectionItems(values, selectionItems, compare, preSelected);
            }
            default:
                return undefined;
        }
    }

    getSelectedItems(selectionItems: SelectionItem[], valueStrategy: ValueStrategyType): SelectionItem[] {
        if (valueStrategy === ValueStrategyType.SelectChildIfOnlyChild) {
            const result = [];
            const selected = selectionItems.filter(si => si.selected);
            selected.forEach(r => {
                if (r.children?.length === 1) {
                    result.push(r.children[0]);
                } else {
                    result.push(r);
                }
            });
            const subRoots = selectionItems.filter(si => !si.selected && si.children).map(si =>
                this.getSelectedItems(si.children, valueStrategy)).reduce((a, b) => a.concat(b), []);
            return result.concat(subRoots);
        } else {
            const result = [];
            result.push(...selectionItems.filter(si => si.selected));
            result.push(...selectionItems.filter(si => si.children).map(si => this.getSelectedItems(si.children, valueStrategy)).reduce((a, b) => a.concat(b), []));

            return result;
        }
    }

    getSelectedRoots(items: SelectionItem[], valueStrategy: ValueStrategyType): SelectionItem[] {
        if (valueStrategy === ValueStrategyType.SelectChildIfOnlyChild) {
            const result = [];
            const selected = items.filter(si => si.selected);
            selected.forEach(s => {
                if (s.children?.length === 1) {
                    result.push(s.children[0]);
                } else {
                    result.push(s);
                }
            });
            const subRoots = items.filter(si => !si.selected && si.children).map(si =>
                this.getSelectedRoots(si.children, valueStrategy)).reduce((a, b) => a.concat(b), []);
            return result.concat(subRoots);
        } else {
            const result = items.filter(si => si.selected);
            const subRoots = items.filter(si => !si.selected && si.children).map(si => this.getSelectedRoots(si.children, valueStrategy)).reduce((a, b) => a.concat(b), []);
            return result.concat(subRoots);
        }
    }

    updateItemRelations(triggerItem: SelectionItem, allItems: SelectionItem[], strategyType: SelectionStrategyType): SelectionEvent[] {
        if (!triggerItem.selected) {
            triggerItem.partialSelected = false;
        }

        const events: SelectionEvent[] = [];
        events.push(new SelectionEvent(triggerItem.selected ? SelectionEventType.Checked : SelectionEventType.Unchecked, triggerItem.value));
        if (triggerItem.children) {
            events.push(...this.toggleTree(triggerItem.children, triggerItem.selected, strategyType));
        }

        if (strategyType === SelectionStrategyType.SelectFirstLeafIfOnlyOne) {
            triggerItem.expanded = triggerItem.selected;
        }

        const parentChain = this.findParents(triggerItem, allItems);
        parentChain.forEach(item => {
            switch (strategyType) {
                case SelectionStrategyType.SelectAll: {
                    SelectionStrategySelectAll.handleParents(item);
                    break;
                }
                case SelectionStrategyType.SelectFirstLeafIfOnlyOne: {
                    SelectionStrategySelectFirstLeafIfOnlyOne.handleParents(item, triggerItem, events);
                    break;
                }
            }
        });

        return events;
    }

    private getTreeCount(items: SelectionItem[]): number {
        return items.length + items.filter(si => si.children).map(si => this.getTreeCount(si.children)).reduce((a, b) => a + b, 0);
    }

    private findParents(item: SelectionItem, items: SelectionItem[]): SelectionItem[] {
        for (const iterator of items.filter(i => i.children)) {
            if (iterator.children.indexOf(item) > -1) {
                return [iterator];
            }

            if (iterator.children) {
                const result = this.findParents(item, iterator.children);
                if (result.length > 0) {
                    result.push(iterator);
                    return result;
                }
            }
        }

        return [];
    }

    private toggleTree(items: SelectionItem[], value: boolean, strategyType: SelectionStrategyType): SelectionEvent[] {
        const events = [];
        items.forEach(i => {
            switch (strategyType) {
                // eslint-disable-next-line sonarjs/no-fallthrough
                case SelectionStrategyType.SelectAll: {
                    events.push(...SelectionStrategySelectAll.toggleTree(items, value));
                }
                case SelectionStrategyType.SelectFirstLeafIfOnlyOne: {
                    events.push(...SelectionStrategySelectFirstLeafIfOnlyOne.toggleTree(items, value));
                }
            }
        });

        return events;
    }
}
